import { GlobalStyles } from "@mui/material";
import { useSignal, useComputed } from "@preact/signals-react";
import { useEffect } from "react";
import { Dialog, Snackbar } from "../../resolvers-types";
import { renderModalPopups } from "../components/ComponentUtils.tsx";
import NGDialog from "../components/NGDialog/NGDialog";
import NGSnackbar from "../components/NGSnackbar/NGSnackbar.tsx";
import NGLayout from "../layouts/NGLayout";
import { publishUserAction } from "../library/analytics.ts";
import { getState, setupHandlers, setupInitialState, setupLocalState, updateItemContext } from "../library/dataService";
import { log } from "../library/logger.ts";
import { INGPageProps } from "../library/NGFieldExtensions";
import tracing from "../library/tracing.ts";
import { updatePageTitle } from "../library/utils.ts";
import { sendReactNativeWebviewLoaded } from "../library/security.ts";

export default function NGPage({ config, layout, context }: INGPageProps) {
  const tag = "NGPage";
  //generate a function to return all the Applications (recursively) and containers (top level only) for a given page
  const dialog: Dialog = { Id: "NGMessage", __typename: "Dialog" };
  const snackbar: Snackbar = { Id: "NGSnackbarMessage", __typename: "Snackbar" };

  context.InDesignMode = config.InDesignMode as boolean;
  const latestPageId = useSignal<string | null | undefined>(null);

  const currentContext = updateItemContext(context, config);

  const local = setupLocalState(
    config,
    {
      InDesignMode: useSignal(config.InDesignMode || false),
      Visible: useSignal(config.Visible ?? true),
      Theme: useSignal(config.Theme ?? "default"),
      Styles: useSignal(config.Styles ?? []),
    },
    currentContext
  );

  setupInitialState(config, currentContext);

  const handlers = setupHandlers(config, currentContext);

  const configWithUniqueIds = useComputed(() => {
    const makeIdsUnique = (obj: any, seenIds = new Set<string>(), inActions = false): any => {
      if (Array.isArray(obj)) {
        return obj.map((item) => makeIdsUnique(item, seenIds, inActions));
      }

      if (obj && typeof obj === "object") {
        const newObj: Record<string, any> = {};
        for (const [key, value] of Object.entries<any>(obj)) {
          if (key === "Id" && !inActions) {
            let newId = value;
            while (seenIds.has(newId)) {
              newId = `${value}_${Math.random().toString(36).substring(2, 8)}`;
            }
            seenIds.add(newId);
            newObj[key] = newId;
          } else if (key === "Actions") {
            newObj[key] = value.map((item: any) => makeIdsUnique(item, seenIds, true));
          } else {
            newObj[key] = makeIdsUnique(value, seenIds, inActions);
          }
        }
        return newObj;
      }

      return obj;
    };

    return makeIdsUnique(config, new Set<string>());
  });

  useEffect(() => {
    const { state } = getState(context);
    const pageId = state.Page?.value.Id;
    const isEditorPage = typeof pageId === "string" && pageId.includes("-editor");
    const basePageName = config.Label ?? config.Name ?? "Page";
    if (!isEditorPage) {
      document.title = basePageName;
      return;
    }

    const editorItemName = state.Page?.value.Items?.[0]?.Name;
    const documentTitle = editorItemName ? `${basePageName} - ${editorItemName}` : basePageName;
    updatePageTitle(documentTitle);
  }, [config.Label, config.Name, context]);

  useEffect(() => {
    if (config.Id === latestPageId.value) return;
    sendReactNativeWebviewLoaded();

    if (handlers["onLoad"]) {
      handlers["onLoad"](config, config, null); // action is null since it's published in loader
    }

    latestPageId.value = config.Id; // Update the latest page ID
    log.info(tag, `ToSave`, configWithUniqueIds.value);
  }, [config, handlers, latestPageId, configWithUniqueIds.value, currentContext]);

  return (
    <>
      <GlobalStyles styles={{ ...local.Styles.value }} />
      <NGDialog key="messageDialog" config={dialog} context={currentContext} />
      <NGSnackbar key="snackbar" config={snackbar} context={currentContext} />
      {renderModalPopups(config, currentContext)}
      {local.Visible.value && layout && (
        <NGLayout
          layout={layout}
          layoutItem={config}
          inDesignMode={local.InDesignMode.value}
          context={currentContext}
        />
      )}
    </>
  );
}
